/**
 * UI-токены для хедера/пазла: высоты, радиусы, цвета, паддинги, шрифты.
 * Один источник правды; применяются в CSS через applyUiTokens().
 */

export const UI_HEADER_TOKENS = {
  /** Высота top bar (px) */
  topbarMinHeight: 56,
  /** Паддинги top bar: вертикальный, горизонтальный (px) */
  topbarPaddingY: 8,
  topbarPaddingX: 16,
  /** Зазор между элементами в top bar (px) */
  topbarGap: 8,
  /** Фон шапки */
  topbarBackground: 'rgba(0, 0, 0, 0.08)',
  topbarShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',

  /** Высота капсул (pill) (px) */
  pillHeight: 40,
  /** Горизонтальный паддинг капсулы (px) */
  pillPaddingX: 12,
  /** Зазор внутри капсулы между иконкой и текстом (px) */
  pillGap: 8,
  /** Радиус капсулы (px) */
  pillBorderRadius: 16,
  /** Фон капсулы */
  pillBackground: 'rgba(255, 255, 255, 0.1)',
  pillShadow: '0 2px 8px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.08)',

  /** Высота progress bar (px) */
  progressBarHeight: 3,
  progressBarBackground: 'rgba(255, 255, 255, 0.08)',
  progressBarFill: 'rgba(255, 255, 255, 0.25)',
  progressBarBorderRadius: 16,

  /** Текст в капсулах */
  capsuleFontSize: '0.875rem',
  capsuleFontWeight: 600,
  capsuleColor: 'rgba(255, 255, 255, 0.95)',
  /** Иконка в капсуле */
  capsuleIconFontSize: '1rem',
  capsuleIconFilter: 'grayscale(1) brightness(1.4) contrast(1.05)',

  /** Icon button (круглая кнопка) */
  iconButtonSize: 40,
  iconButtonBorderRadius: '50%',
  iconButtonBackground: 'rgba(255, 255, 255, 0.08)',
  iconButtonBackgroundHover: 'rgba(255, 255, 255, 0.12)',
  iconButtonBackgroundActive: 'rgba(255, 255, 255, 0.06)',
  iconButtonColor: 'rgba(255, 255, 255, 0.9)',
  iconButtonScaleHover: 0.98,
  iconButtonScaleActive: 0.95,
  iconButtonTransition: 'background 0.15s ease, transform 0.15s ease',

  /** Карточка (Card/Panel) — как у подборок/пазла */
  cardBackground: 'rgba(51, 51, 51, 1)',
  cardBorderRadius: 12,
  cardShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  cardInnerPaddingPx: 18,
  cardGapPx: 16,
  /** Превью внутри карточки: скругление как у карточек подборок */
  previewBorderRadius: 12,

  /** Текст в карточке подборки (PackCard) */
  cardTitleFontSize: '0.95rem',
  cardTitleColor: 'rgba(255, 255, 255, 0.95)',
  cardProgressFontSize: '0.85rem',
  cardProgressColor: 'rgba(170, 170, 170, 1)',

  /** Семантические стили текста (UiTheme textStyle) */
  textPrimary: 'rgba(255, 255, 255, 1)',
  textPrimaryFontSize: '1rem',
  textSecondary: 'rgba(255, 255, 255, 0.82)',
  textSecondaryFontSize: '0.85rem',

  /** Заголовок секции (daily «Пазл дня») — меньше и аккуратнее */
  headlineSmFontSize: '0.95rem',
  headlineSmFontWeight: 600,
  headlineSmColor: 'rgba(255, 255, 255, 0.95)',
  /** Метка/статус — заметный, не H1 */
  labelFontSize: '0.875rem',
  labelFontWeight: 600,
  labelColor: 'rgba(255, 255, 255, 0.9)',
  /** Приглушённый текст (таймер, подписи) */
  mutedFontSize: '0.75rem',
  mutedColor: 'rgba(170, 170, 170, 1)',

  /** Малый вертикальный зазор (между статусом/таймером/кнопкой в dailyCard) */
  gapSmPx: 8,
  /** Мелкий текст (таймер «До завтра») */
  countdownFontSize: '0.75rem',
  countdownColor: 'rgba(170, 170, 170, 1)',

  /** CTA / акцентная кнопка (daily «Начать пазл» и др.): не синяя, в стиле игры */
  accentButtonBg: 'rgba(255, 255, 255, 0.16)',
  accentButtonBgActive: 'rgba(255, 255, 255, 0.1)',
  accentButtonColor: 'rgba(255, 255, 255, 0.98)',
  accentButtonScaleActive: 0.98,
  accentButtonBorderRadius: 10,
} as const;

/** Применяет токены как CSS-переменные на элемент (обычно #ui-root). */
export function applyUiTokens(root: HTMLElement): void {
  const t = UI_HEADER_TOKENS;
  root.style.setProperty('--ui-topbar-min-height', `${t.topbarMinHeight}px`);
  root.style.setProperty('--ui-topbar-padding-y', `${t.topbarPaddingY}px`);
  root.style.setProperty('--ui-topbar-padding-x', `${t.topbarPaddingX}px`);
  root.style.setProperty('--ui-topbar-gap', `${t.topbarGap}px`);
  root.style.setProperty('--ui-topbar-background', t.topbarBackground);
  root.style.setProperty('--ui-topbar-shadow', t.topbarShadow);

  root.style.setProperty('--ui-pill-height', `${t.pillHeight}px`);
  root.style.setProperty('--ui-pill-padding-x', `${t.pillPaddingX}px`);
  root.style.setProperty('--ui-pill-gap', `${t.pillGap}px`);
  root.style.setProperty('--ui-pill-border-radius', `${t.pillBorderRadius}px`);
  root.style.setProperty('--ui-pill-background', t.pillBackground);
  root.style.setProperty('--ui-pill-shadow', t.pillShadow);

  root.style.setProperty('--ui-progress-bar-height', `${t.progressBarHeight}px`);
  root.style.setProperty('--ui-progress-bar-bg', t.progressBarBackground);
  root.style.setProperty('--ui-progress-bar-fill', t.progressBarFill);
  root.style.setProperty('--ui-progress-bar-radius', `${t.progressBarBorderRadius}px`);

  root.style.setProperty('--ui-capsule-font-size', t.capsuleFontSize);
  root.style.setProperty('--ui-capsule-font-weight', String(t.capsuleFontWeight));
  root.style.setProperty('--ui-capsule-color', t.capsuleColor);
  root.style.setProperty('--ui-capsule-icon-font-size', t.capsuleIconFontSize);
  root.style.setProperty('--ui-capsule-icon-filter', t.capsuleIconFilter);

  root.style.setProperty('--ui-icon-btn-size', `${t.iconButtonSize}px`);
  root.style.setProperty('--ui-icon-btn-radius', t.iconButtonBorderRadius);
  root.style.setProperty('--ui-icon-btn-bg', t.iconButtonBackground);
  root.style.setProperty('--ui-icon-btn-bg-hover', t.iconButtonBackgroundHover);
  root.style.setProperty('--ui-icon-btn-bg-active', t.iconButtonBackgroundActive);
  root.style.setProperty('--ui-icon-btn-color', t.iconButtonColor);
  root.style.setProperty('--ui-icon-btn-scale-hover', String(t.iconButtonScaleHover));
  root.style.setProperty('--ui-icon-btn-scale-active', String(t.iconButtonScaleActive));
  root.style.setProperty('--ui-icon-btn-transition', t.iconButtonTransition);

  root.style.setProperty('--ui-card-bg', t.cardBackground);
  root.style.setProperty('--ui-card-radius', `${t.cardBorderRadius}px`);
  root.style.setProperty('--ui-card-shadow', t.cardShadow);
  root.style.setProperty('--ui-card-inner-padding', `${t.cardInnerPaddingPx}px`);
  root.style.setProperty('--ui-card-gap', `${t.cardGapPx}px`);
  root.style.setProperty('--ui-preview-radius', `${t.previewBorderRadius}px`);

  root.style.setProperty('--ui-card-title-font-size', t.cardTitleFontSize);
  root.style.setProperty('--ui-card-title-color', t.cardTitleColor);
  root.style.setProperty('--ui-card-progress-font-size', t.cardProgressFontSize);
  root.style.setProperty('--ui-card-progress-color', t.cardProgressColor);

  root.style.setProperty('--ui-text-primary', t.textPrimary);
  root.style.setProperty('--ui-text-primary-font-size', t.textPrimaryFontSize);
  root.style.setProperty('--ui-text-secondary', t.textSecondary);
  root.style.setProperty('--ui-text-secondary-font-size', t.textSecondaryFontSize);

  root.style.setProperty('--ui-headline-sm-font-size', t.headlineSmFontSize);
  root.style.setProperty('--ui-headline-sm-font-weight', String(t.headlineSmFontWeight));
  root.style.setProperty('--ui-headline-sm-color', t.headlineSmColor);
  root.style.setProperty('--ui-label-font-size', t.labelFontSize);
  root.style.setProperty('--ui-label-font-weight', String(t.labelFontWeight));
  root.style.setProperty('--ui-label-color', t.labelColor);
  root.style.setProperty('--ui-muted-font-size', t.mutedFontSize);
  root.style.setProperty('--ui-muted-color', t.mutedColor);

  root.style.setProperty('--ui-gap-sm', `${t.gapSmPx}px`);
  root.style.setProperty('--ui-countdown-font-size', t.countdownFontSize);
  root.style.setProperty('--ui-countdown-color', t.countdownColor);

  root.style.setProperty('--ui-accent-button-bg', t.accentButtonBg);
  root.style.setProperty('--ui-accent-button-bg-active', t.accentButtonBgActive);
  root.style.setProperty('--ui-accent-button-color', t.accentButtonColor);
  root.style.setProperty('--ui-accent-button-scale-active', String(t.accentButtonScaleActive));
  root.style.setProperty('--ui-accent-button-radius', `${t.accentButtonBorderRadius}px`);
}
